/**
 * Reglas del formulario de usuarios de Ajustes (petición de Robert, 01/10/2026). Funciones
 * puras: la base de datos y la función `users` vuelven a comprobar lo importante (solo un
 * administrador da de alta, nadie se quita su propio acceso, siempre queda un administrador).
 */

export type ModoAcceso = "contrasena" | "invitacion";

export type DatosUsuario = {
  first_name: string;
  last_name: string;
  email: string;
  administrator: boolean;
  disabled: boolean;
  /** Solo en el alta. */
  modo?: ModoAcceso;
  /** Solo en el alta con contraseña provisional. */
  password?: string;
  /** 30 (Robert, 16/10/2026): cambiar el email de un usuario, escrito dos veces. Vacío = no cambia. */
  emailNuevo?: string;
  emailRepetido?: string;
};

/** Errores por campo, con clave i18n (`nevora.usuarios.errores.*`). */
export type ErroresUsuario = Partial<Record<keyof DatosUsuario, string>>;

export const LONGITUD_MINIMA_CONTRASENA = 8;

const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const validarUsuario = (
  datos: DatosUsuario,
  { alta, esUnoMismo }: { alta: boolean; esUnoMismo: boolean },
): ErroresUsuario => {
  const errores: ErroresUsuario = {};
  if (!datos.first_name.trim())
    errores.first_name = "nevora.usuarios.errores.falta_nombre";
  if (alta && !RE_EMAIL.test(datos.email.trim()))
    errores.email = "nevora.usuarios.errores.email";
  if (
    alta &&
    datos.modo === "contrasena" &&
    (datos.password ?? "").length < LONGITUD_MINIMA_CONTRASENA
  )
    errores.password = "nevora.usuarios.errores.contrasena_corta";
  if (esUnoMismo && !datos.administrator)
    errores.administrator = "nevora.usuarios.errores.quitarse_admin";
  if (esUnoMismo && datos.disabled)
    errores.disabled = "nevora.usuarios.errores.desactivarse";
  const nuevo = (datos.emailNuevo ?? "").trim().toLowerCase();
  if (!alta && nuevo) {
    if (!RE_EMAIL.test(nuevo))
      errores.emailNuevo = "nevora.usuarios.errores.email";
    else if (nuevo === datos.email.trim().toLowerCase())
      errores.emailNuevo = "nevora.usuarios.errores.email_igual";
    else if (nuevo !== (datos.emailRepetido ?? "").trim().toLowerCase())
      errores.emailRepetido = "nevora.usuarios.errores.email_no_coincide";
  }
  return errores;
};

/**
 * Traduce el error de la función `users` a una clave i18n. La función devuelve `code` en
 * los errores conocidos; la invitación sin SMTP llega como error de envío de correo.
 */
export const claveErrorUsuarios = (error: unknown): string => {
  const e = error as { code?: string; message?: string } | undefined;
  switch (e?.code) {
    case "email_taken":
    case "email_exists":
      return "nevora.usuarios.errores.email_usado";
    case "weak_password":
      return "nevora.usuarios.errores.contrasena_corta";
    case "self_lockout":
      return "nevora.usuarios.errores.quitarse_admin";
    case "last_admin":
      return "nevora.usuarios.errores.ultimo_admin";
    case "tiene_datos":
      return "nevora.usuarios.errores.tiene_datos";
    case "self_delete":
      return "nevora.usuarios.errores.borrarse";
    case "no_borrable":
      return "nevora.usuarios.errores.no_borrable";
    case "foto_ajena":
      return "nevora.usuarios.errores.foto";
  }
  if (/mail|smtp|invit/i.test(e?.message ?? ""))
    return "nevora.usuarios.errores.sin_correo";
  return "nevora.usuarios.errores.desconocido";
};

/** Lo que tiene asociado un usuario (30, función `users` → 409 «tiene_datos»), en orden de lectura. */
export const ASOCIADO_ORDEN = [
  "fichas_asignadas",
  "fichas_creadas",
  "notas",
  "proyectos",
  "asignaciones",
  "cobros",
  "equipo",
  "fichas_borradas",
  "peticiones_leads",
] as const;

export const lineasAsociado = (
  asociado: Record<string, number> | null | undefined,
): { clave: string; n: number }[] =>
  ASOCIADO_ORDEN.filter((k) => Number(asociado?.[k]) > 0).map((k) => ({
    clave: `nevora.usuarios.asociado.${k}`,
    n: Number(asociado![k]),
  }));
